function requireEnv(key: string): string {
  const value = import.meta.env[key] as string | undefined;
  if (!value) throw new Error(`Missing required env var: ${key}`);
  return value;
}

function optionalEnv(key: string, fallback: string): string {
  return (import.meta.env[key] as string | undefined) ?? fallback;
}

function boolEnv(key: string, fallback: boolean): boolean {
  const value = (import.meta.env[key] as string | undefined)
    ?.trim()
    .toLowerCase();
  if (value === undefined || value === '') return fallback;
  return value === 'true' || value === '1';
}

export const config = {
  apiBaseUrl: optionalEnv('VITE_API_BASE_URL', '/api'),
  /** Independently reachable API for Agency; browser-only proxy URLs cannot be used by the desktop app. */
  agencyApiBaseUrl: optionalEnv(
    'VITE_AGENCY_API_BASE_URL',
    optionalEnv('VITE_API_BASE_URL', '/api'),
  ),
  /**
   * When true, the Workflow API client acquires an MSAL token and sends it
   * as an Authorization header. Defaults to true — production must always
   * carry a real bearer token. Set to false in .env.local for local dev
   * with the API's anonymous auth handler.
   */
  apiAuthEnabled: boolEnv('VITE_API_AUTH_ENABLED', true),
  entraClientId: requireEnv('VITE_ENTRA_CLIENT_ID'),
  entraTenantId: requireEnv('VITE_ENTRA_TENANT_ID'),
  datalakeServiceAppId: requireEnv('VITE_DATALAKE_SERVICE_APP_ID'),
  datalakeTenantId: optionalEnv(
    'VITE_DATALAKE_TENANT_ID',
    requireEnv('VITE_ENTRA_TENANT_ID'),
  ),
  datalakeApiBaseUrl: requireEnv('VITE_DATALAKE_API_BASE_URL'),
  /**
   * When true, instrumented DataHub/TEDH calls attach the
   * `X-TEDH-Usage-Context` request header (see the Datasets usage-tracking
   * requirements doc, §8). Defaults to false so the header is emitted ONLY
   * where explicitly opted in.
   *
   * Enabled in `.env.localdev` and in `.env.nonprod` (the `build:nonprod`
   * bundle deployed to the dev site https://aistudio.teams.core.dev.microsoft);
   * ppe / prod leave it unset (false) so no usage-context header is sent from
   * those environments.
   */
  tedhUsageHeaderEnabled: boolEnv('VITE_TEDH_USAGE_HEADER_ENABLED', false),
  workflowApiServiceAppId: requireEnv('VITE_WORKFLOW_API_SERVICE_APP_ID'),

  // ── Teams Pulse (embedded Trends dashboard) ─────────────────────────────
  /**
   * Base URL of the Teams Pulse webapp. The Trends page embeds a dashboard
   * from this host in an iframe. Teams Pulse's CSP `frame-ancestors` must
   * allowlist the AI Studio origin per env for the iframe to render.
   */
  teamsPulseBaseUrl: optionalEnv(
    'VITE_TEAMS_PULSE_BASE_URL',
    'https://pulse.teams.core.microsoft',
  ),
  /**
   * Default Teams Pulse dashboard used when the active workspace has no
   * dashboard configured. Overridable per environment.
   */
  teamsPulseDashboardId: optionalEnv(
    'VITE_TEAMS_PULSE_DASHBOARD_ID',
    '01aec048-0acd-4817-99f0-050f19c0e09b',
  ),
  /**
   * Name of the Teams Pulse dashboard filter parameter that receives the
   * AI Studio active workspace. Must match the parameter name defined on the
   * Teams Pulse dashboard itself (visible in the dashboard's filter bar URL,
   * e.g. `?workspaceName=foo` → `'workspaceName'`). Change this per-env only
   * if the dashboard is re-authored with a different parameter name.
   */
  teamsPulseWorkspaceParam: optionalEnv(
    'VITE_TEAMS_PULSE_WORKSPACE_PARAM',
    'workspaceName',
  ),

  // ── Frontend telemetry (Teams Standalone Logger → 1DS/Aria → Geneva) ──────
  /**
   * Aria ingestion token (iKey) for this app's Aria project. Self-served from
   * the Aria portal (Project → Manage Ingestion → Keys) — differs per ring so
   * prod and nonprod data land in separate Aria projects. Not a secret: it is
   * baked into the client bundle, same as the Entra client IDs above.
   *
   * When empty the logger is never initialized and all telemetry calls become
   * no-ops — this is the single switch that enables/disables telemetry.
   */
  telemetryInstrumentationKey: optionalEnv(
    'VITE_TELEMETRY_INSTRUMENTATION_KEY',
    '',
  ),
  /**
   * When true the logger routes events to the console (DebugSender) instead of
   * shipping to OneCollector. Enabled in localdev so local runs don't pollute
   * the shared Aria project.
   */
  telemetryDebug: boolEnv('VITE_TELEMETRY_DEBUG', false),
  /**
   * OneCollector regional endpoint selector. Commercial rings use 'prod'
   * (teams.events.data.microsoft.com); override only for sovereign clouds
   * (e.g. 'gcc', 'gcch', 'dod'). Prod-vs-nonprod is distinguished by the iKey,
   * not this value.
   */
  telemetryEnvironment: optionalEnv('VITE_TELEMETRY_ENVIRONMENT', 'prod'),
  /** Stable app identifier attached to every telemetry event. */
  telemetryAppName: optionalEnv('VITE_TELEMETRY_APP_NAME', 'ai-studio-webapp'),
  /**
   * The app ring this build targets, taken from the Vite mode
   * (`localdev` | `development` | `nonprod` | `prod`). Stamped as custom data
   * onto every telemetry event so we can tell which environment it came from.
   */
  appEnvironment: import.meta.env.MODE,
  /**
   * Unique frontend build identifier stamped by CI. Local builds use a
   * recognizable fallback because they are not deployed artifacts.
   */
  appVersion: optionalEnv('VITE_APP_VERSION', 'local'),
} as const;

/**
 * True in a Vite dev build (`vite dev`), false in production bundles. A
 * build-time constant Vite statically replaces, so guarded dev-only code (e.g.
 * React Query Devtools) tree-shakes out of prod. Read this instead of
 * `import.meta.env.DEV` in components.
 */
export const isDevMode: boolean = import.meta.env.DEV;
